import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  BookOpen,
  FileText,
  Search,
  ChevronDown,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Loader2,
  Wand2,
  Play,
  Globe,
  GlobeOff,
  Trash2,
  Copy,
  Pencil
} from 'lucide-react';
import toast from 'react-hot-toast';
import { storiesApi, createEventSource } from '../../api/client';
import MiniPagePreview from './MiniPagePreview';
import ConfirmDialog from '../ConfirmDialog';

const PLAYER_URL = process.env.REACT_APP_PLAYER_URL || '';
const PAGE_SIZE = 20;

const STAGE_INFO = {
  planning: { icon: '🎨', label: 'Planning story…' },
  'fetching-media': { icon: '🖼️', label: 'Fetching images…' },
  generating: { icon: '✨', label: 'Generating story…' },
  qa: { icon: '🔍', label: 'Quality check…' },
  repairing: { icon: '🔧', label: 'Repairing issues…' },
  done: { icon: '✅', label: 'Story ready!' },
  error: { icon: '❌', label: 'Generation failed' },
  connected: { icon: '🔗', label: 'Connected…' },
};

export default function StoriesTab({ createStory, creating, aiAllowed = true }) {
  const navigate = useNavigate();

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [storiesPage, setStoriesPage] = useState(1);
  const [storiesTotal, setStoriesTotal] = useState(0);
  const [globalStats, setGlobalStats] = useState({ total: 0, published: 0, draft: 0, archived: 0 });
  const [publishing, setPublishing] = useState({});
  const [unpublishing, setUnpublishing] = useState({});

  const [aiTopic, setAiTopic] = useState('');
  const [aiTone, setAiTone] = useState('');
  const [aiAudience, setAiAudience] = useState('');
  const [aiColorPref, setAiColorPref] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiStage, setAiStage] = useState('');
  const [aiStageMsg, setAiStageMsg] = useState('');
  const [aiIteration, setAiIteration] = useState(0);
  const [aiQualityScore, setAiQualityScore] = useState(null);
  const [aiShowOptions, setAiShowOptions] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const aiSseRef = useRef(null);
  const aiPollRef = useRef(null);
  const aiNavDone = useRef(false);
  const aiInputRef = useRef(null);


  const apiKey = localStorage.getItem('api-key');
  const accessToken = localStorage.getItem('customer-portal');

  // Load aggregate stats once on mount
  useEffect(() => {
    loadGlobalStats();
    return () => {
      if (aiSseRef.current) aiSseRef.current.close();
      if (aiPollRef.current) clearInterval(aiPollRef.current);
    };
  }, []);

  // Debounce search input — 400 ms delay before firing API call
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setStoriesPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Re-fetch stories whenever page, filter, or debounced search changes
  useEffect(() => {
    loadStories(storiesPage, filter, debouncedSearch);
  }, [storiesPage, filter, debouncedSearch]);

  async function loadGlobalStats() {
    try {
      const [all, pub, draft, archived] = await Promise.all([
        storiesApi.list(1, 1),
        storiesApi.list(1, 1, { status: 'published' }),
        storiesApi.list(1, 1, { status: 'draft' }),
        storiesApi.list(1, 1, { status: 'archived' }),
      ]);
      setGlobalStats({
        total: all.meta?.total ?? 0,
        published: pub.meta?.total ?? 0,
        draft: draft.meta?.total ?? 0,
        archived: archived.meta?.total ?? 0,
      });
    } catch { /* ignore — stats are non-critical */ }
  }

  async function loadStories(page = 1, filterVal = 'all', searchVal = '') {
    setLoading(true);
    try {
      const params = {};
      if (filterVal !== 'all') params.status = filterVal;
      if (searchVal.trim()) params.search = searchVal.trim();

      const result = await storiesApi.list(page, PAGE_SIZE, params);
      const raw = result.data || [];
      const meta = result.meta || {};

      const mapped = raw.map(s => {
        const previewPage = s.pages?.find(p => (p.layers || []).length > 0) ?? s.pages?.[0];
        return {
          ...s,
          title: s.title || 'Untitled Story',
          pageCount: s.pages?.length ?? 0,
          firstPageBackground: previewPage?.background,
          firstPageLayers: (previewPage?.layers || []).map((l, i) => ({
            ...l,
            id: l.id || l._id || `layer-${i}`,
          })),
        };
      });
      setStories(mapped);
      setStoriesTotal(meta.total ?? 0);
    } catch {
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  }

  function stopAiGeneration() {
    if (aiSseRef.current) { aiSseRef.current.close(); aiSseRef.current = null; }
    if (aiPollRef.current) { clearInterval(aiPollRef.current); aiPollRef.current = null; }
    setAiGenerating(false);
    setAiStage('');
    setAiStageMsg('');
  }

  function navigateToEditor(id) {
    if (aiNavDone.current) return;
    aiNavDone.current = true;
    // Stop SSE/polling but keep the progress panel open during the preparing delay
    if (aiSseRef.current) { aiSseRef.current.close(); aiSseRef.current = null; }
    if (aiPollRef.current) { clearInterval(aiPollRef.current); aiPollRef.current = null; }
    setAiTopic(''); setAiTone(''); setAiAudience(''); setAiColorPref('');
    // Mark all stages done and show the preparing message
    setAiStage('done');
    setAiStageMsg('🚀 Preparing edit sandbox…');
    setTimeout(() => {
      setAiGenerating(false);
      setAiStage('');
      setAiStageMsg('');
      loadStories();
      navigate(`/marketing/story-builder/${id}/edit`);
    }, 3000);
  }

  async function generateWithAI() {
    if (!aiTopic.trim() || aiGenerating) return;
    setAiGenerating(true);
    setAiStage('planning');
    setAiStageMsg('🎨 Planning story…');
    setAiIteration(0);
    setAiQualityScore(null);
    aiNavDone.current = false;

    let storyId = null;
    try {
      const story = await storiesApi.aiGenerate({
        topic: aiTopic.trim(),
        tone: aiTone.trim() || undefined,
        targetAudience: aiAudience.trim() || undefined,
        colorPreference: aiColorPref.trim() || undefined,
      });
      storyId = story.id;

      aiPollRef.current = setInterval(async () => {
        try {
          const s = await storiesApi.get(storyId);
          const meta = s.metadata;
          if (!meta.aiGenerating) {
            if (meta.aiError) { stopAiGeneration(); toast.error(meta.aiError || 'Generation failed'); }
            else { navigateToEditor(storyId); }
          }
        } catch { /* ignore */ }
      }, 8000);

      const evt = createEventSource(`/stories/${storyId}/progress`);
      aiSseRef.current = evt;

      evt.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          const info = STAGE_INFO[data.stage] || { icon: '⏳', label: data.stage };
          setAiStage(data.stage);
          setAiStageMsg(`${info.icon} ${data.message}`);
          if (data.iteration) setAiIteration(data.iteration);
          if (data.qualityScore != null) setAiQualityScore(data.qualityScore);
          if (data.stage === 'done') navigateToEditor(storyId);
          if (data.stage === 'error') { stopAiGeneration(); toast.error(data.message || 'Generation failed'); }
        } catch { /* ignore */ }
      };

      evt.onerror = () => {
        if (aiSseRef.current) { aiSseRef.current.close(); aiSseRef.current = null; }
      };
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'AI generation failed');
      stopAiGeneration();
    }
  }

  function deleteStory(id, e) {
    e.stopPropagation();
    setConfirmDeleteId(id);
  }

  async function doDeleteStory() {
    try {
      await storiesApi.delete(confirmDeleteId);
      toast.success('Story deleted');
      loadStories(storiesPage, filter, search);
      loadGlobalStats();
    } catch { toast.error('Failed to delete'); }
    finally { setConfirmDeleteId(null); }
  }

  async function publishStoryCard(id, e) {
    e.stopPropagation();
    if (publishing[id]) return;
    setPublishing(prev => ({ ...prev, [id]: true }));
    try {
      await storiesApi.publish(id);
      setStories(prev => prev.map(s => s.id === id ? { ...s, status: 'published' } : s));
      toast.success(
        <div className="flex items-center gap-3">
          <div>
            <p className="font-semibold text-sm">Story published!</p>
            <p className="text-xs opacity-70">Ready to share</p>
          </div>
          <button
            onClick={() => {
              const s = stories.find(x => x.id === id);
              window.open(s?.metadata?.cloudflareUrl || s?.metadata?.publishedUrl || `${PLAYER_URL}/${id}`, '_blank');
            }}
            className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{ background: 'var(--brand-primary)', color: '#fff' }}
          >
            View Story
          </button>
        </div>,
        { duration: 8000 }
      );
    } catch { toast.error('Publish failed'); }
    finally { setPublishing(prev => ({ ...prev, [id]: false })); }
  }

  async function unpublishStoryCard(id, e) {
    e.stopPropagation();
    if (unpublishing[id]) return;
    setUnpublishing(prev => ({ ...prev, [id]: true }));
    try {
      await storiesApi.unpublish(id);
      setStories(prev => prev.map(s => s.id === id ? { ...s, status: 'archived' } : s));
      toast.success('Story unpublished');
      loadGlobalStats();
    } catch { toast.error('Unpublish failed'); }
    finally { setUnpublishing(prev => ({ ...prev, [id]: false })); }
  }

  async function duplicateStory(id, e) {
    e.stopPropagation();
    try {
      await storiesApi.duplicate(id);
      toast.success('Story duplicated!');
      loadStories(storiesPage, filter, search);
      loadGlobalStats();
    } catch { toast.error('Failed to duplicate'); }
  }

  const totalServerPages = Math.max(1, Math.ceil(storiesTotal / PAGE_SIZE));

  return (
    <>
      {/* AI Story Builder */}
      {aiAllowed && <div className="mx-0 my-6 relative overflow-hidden rounded-2xl" style={{
        background: '#ffffff',
        border: '1px solid #e8eaf0',
        boxShadow: '0 4px 24px rgba(99,102,241,0.08), 0 1px 4px rgba(0,0,0,0.06)',
      }}>
        {/* subtle top accent bar */}
        <div style={{ height: 3, background: 'linear-gradient(90deg, var(--brand-primary), #8b5cf6, #a855f7)' }} />
        {/* soft tinted bg wash */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 90% 0%, rgba(99,102,241,0.05) 0%, transparent 60%)',
        }} />

        <div className="relative p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="flex items-center gap-2.5 mb-1.5">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), #8b5cf6)', boxShadow: '0 4px 12px rgba(99,102,241,0.35)' }}>
                  <Wand2 size={17} style={{ color: '#fff' }} />
                </div>
                <h2 style={{
                  background: 'linear-gradient(90deg, var(--brand-900) 0%, var(--brand-primary) 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  fontSize: '20px', fontWeight: 700, letterSpacing: '-0.4px',
                  fontFamily: 'Inter, sans-serif',
                }}>What's Your Story?</h2>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{
                  background: 'linear-gradient(135deg, var(--brand-primary), #8b5cf6)',
                  color: '#fff', letterSpacing: 1,
                }}>AI</span>
              </div>
              <p style={{ color: '#64748b', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                Describe an idea and watch it come to life as a rich, visual web story
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-xs flex-shrink-0 ml-4" style={{ color: '#94a3b8' }}>
              <span className="flex items-center gap-1"><Sparkles size={11} style={{ color: 'var(--brand-primary)' }} /> 5–8 pages</span>
              <span style={{ opacity: 0.4 }}>·</span>
              <span>Fully editable</span>
            </div>
          </div>

          {/* Input row */}
          <div className="relative flex gap-2">
            <input
              ref={aiInputRef}
              type="text"
              value={aiTopic}
              onChange={e => setAiTopic(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') generateWithAI(); }}
              placeholder="e.g. Top 5 EV cars in Pakistan 2026, Summer fashion trends…"
              disabled={aiGenerating}
              className="flex-1 rounded-xl px-4"
              style={{
                background: '#f8f9ff',
                border: '1.5px solid #e2e5f0',
                color: '#1e293b',
                outline: 'none',
                height: '44px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e2e5f0'; e.currentTarget.style.boxShadow = 'none'; }}
            />
            <button
              type="button"
              onClick={() => setAiShowOptions(v => !v)}
              disabled={aiGenerating}
              className="flex items-center gap-1.5 px-3 rounded-xl text-sm font-medium transition-all flex-shrink-0"
              style={{
                background: aiShowOptions ? 'var(--brand-50)' : '#f8f9ff',
                color: aiShowOptions ? 'var(--brand-primary)' : '#64748b',
                border: `1.5px solid ${aiShowOptions ? 'var(--brand-200)' : '#e2e5f0'}`,
                height: '44px',
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
              }}
            >
              <ChevronDown size={13} style={{ transform: aiShowOptions ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              Options
            </button>
            <button
              onClick={generateWithAI}
              disabled={aiGenerating || !aiTopic.trim()}
              className="flex items-center gap-2 px-5 rounded-xl font-semibold transition-all flex-shrink-0"
              style={{
                background: aiGenerating || !aiTopic.trim()
                  ? '#f1f5f9'
                  : 'linear-gradient(135deg, var(--brand-primary) 0%, #8b5cf6 100%)',
                color: aiGenerating || !aiTopic.trim() ? '#94a3b8' : '#fff',
                cursor: aiGenerating || !aiTopic.trim() ? 'not-allowed' : 'pointer',
                boxShadow: aiGenerating || !aiTopic.trim() ? 'none' : '0 4px 14px rgba(99,102,241,0.38)',
                border: '1.5px solid transparent',
                height: '44px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '-0.1px',
              }}
            >
              {aiGenerating ? (
                <><Loader2 size={14} className="animate-spin" /> Generating…</>
              ) : (
                <><Wand2 size={14} /> Generate <ArrowRight size={12} /></>
              )}
            </button>
          </div>

          {/* Options panel */}
          {aiShowOptions && !aiGenerating && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: 'Tone', placeholder: 'e.g. bold, elegant, playful…', value: aiTone, set: setAiTone },
                { label: 'Audience', placeholder: 'e.g. young professionals, parents…', value: aiAudience, set: setAiAudience },
                { label: 'Color style', placeholder: 'e.g. dark & neon, warm earth tones…', value: aiColorPref, set: setAiColorPref },
              ].map(({ label, placeholder, value, set }) => (
                <div key={label}>
                  <label className="text-xs mb-1.5 block" style={{ color: '#475569', fontWeight: 600, fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}>{label}</label>
                  <input
                    type="text"
                    value={value}
                    onChange={e => set(e.target.value)}
                    placeholder={placeholder}
                    className="w-full rounded-xl px-3"
                    style={{
                      background: '#f8f9ff',
                      border: '1.5px solid #e2e5f0',
                      color: '#1e293b',
                      outline: 'none',
                      height: '36px',
                      fontSize: '13px',
                      fontFamily: 'Inter, sans-serif',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.10)'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#e2e5f0'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Progress panel */}
          {aiGenerating && (
            <div className="mt-4 p-4 rounded-xl" style={{ background: '#f8f9ff', border: '1.5px solid #e2e5f0' }}>
              <div className="flex items-center gap-1 mb-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {(['planning', 'fetching-media', 'generating', 'qa', 'repairing', 'done']).map((s, idx, arr) => {
                  const icons = { planning: '🎨', 'fetching-media': '🖼️', generating: '✨', qa: '🔍', repairing: '🔧', done: '✅' };
                  const stages = ['planning', 'fetching-media', 'generating', 'qa', 'repairing', 'done'];
                  const currentIdx = stages.indexOf(aiStage);
                  const isActive = aiStage === s;
                  const isDone = currentIdx > idx;
                  return (
                    <div key={s} className="flex items-center gap-1 flex-shrink-0">
                      <div className="flex flex-col items-center">
                        <span className="text-base leading-none" style={{ opacity: isActive ? 1 : isDone ? 0.85 : 0.3 }}>{icons[s]}</span>
                        <span className="mt-0.5" style={{ fontSize: 10, color: isActive ? 'var(--brand-primary)' : '#94a3b8', fontWeight: isActive ? 700 : 400, fontFamily: 'Inter, sans-serif', letterSpacing: '0.01em' }}>
                          {s.replace('-', ' ')}
                        </span>
                      </div>
                      {idx < arr.length - 1 && (
                        <div className="w-5 h-px mx-0.5 flex-shrink-0" style={{
                          background: isDone ? 'linear-gradient(90deg, var(--brand-primary), #8b5cf6)' : '#e2e5f0',
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Loader2 size={13} className="animate-spin flex-shrink-0" style={{ color: 'var(--brand-primary)' }} />
                <span style={{ color: '#1e293b', fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{aiStageMsg}</span>
                {aiIteration > 0 && <span style={{ color: '#94a3b8', fontSize: '12px', fontFamily: 'Inter, sans-serif' }}>(attempt {aiIteration}/5)</span>}
                {aiQualityScore != null && <span style={{ color: '#94a3b8', fontSize: '12px', fontFamily: 'Inter, sans-serif', marginLeft: 'auto' }}>score: {aiQualityScore}</span>}
              </div>
            </div>
          )}

          {/* Suggestion chips */}
          {!aiGenerating && (
            <div className="mt-3 flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              <span className="flex-shrink-0" style={{ color: '#94a3b8', fontSize: '12px', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>Try:</span>
              {[
                'A guide to healthy morning routines', 'Top 5 electric cars of 2025',
                'Summer fashion trends for women', 'How to start investing in stocks',
                'A day trip to Paris highlights', 'Fitness tips for busy professionals',
                '10 must-try street foods in Tokyo', 'Skincare routine for glowing skin',
                'Best budget laptops for students', 'Home workout plan for beginners',
                'Travel guide to Bali on a budget', 'How to brew the perfect coffee',
                'Top smartphone cameras of 2026', 'Ramadan recipes from around the world',
                'Minimalist wardrobe essentials', 'Digital marketing tips for startups',
              ].map(chip => (
                <button
                  key={chip}
                  onClick={() => { setAiTopic(chip); aiInputRef.current?.focus(); }}
                  className="px-3 py-1 rounded-full flex-shrink-0 transition-all"
                  style={{ background: '#f1f5ff', border: '1px solid #e0e4f5', color: '#64748b', whiteSpace: 'nowrap', fontSize: '12px', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--brand-50)'; e.currentTarget.style.borderColor = 'var(--brand-200)'; e.currentTarget.style.color = 'var(--brand-primary)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f1f5ff'; e.currentTarget.style.borderColor = '#e0e4f5'; e.currentTarget.style.color = '#64748b'; }}
                >
                  {chip}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>}

      {/* Stats row */}
      {!loading && globalStats.total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 mx-0">
          {[
            { label: 'Total Stories', value: globalStats.total,     icon: <BookOpen size={18} />,     color: 'var(--brand-primary)', bg: 'var(--brand-50)', shadow: 'rgba(99,102,241,0.12)', grad: 'linear-gradient(135deg,var(--brand-primary),var(--brand-400))', sub: 'All time' },
            { label: 'Published',     value: globalStats.published, icon: <CheckCircle2 size={18} />, color: '#10b981', bg: '#ecfdf5', shadow: 'rgba(16,185,129,0.12)', grad: 'linear-gradient(135deg,#10b981,#34d399)', sub: globalStats.total ? `${Math.round(globalStats.published / globalStats.total * 100)}% of total` : '—' },
            { label: 'Drafts',        value: globalStats.draft,     icon: <FileText size={18} />,     color: '#f59e0b', bg: '#fffbeb', shadow: 'rgba(245,158,11,0.12)', grad: 'linear-gradient(135deg,#f59e0b,#fbbf24)', sub: 'In progress' },
          ].map(({ label, value, icon, color, bg, shadow, grad, sub }) => (
            <div key={label} className="flex flex-col gap-4 p-5 rounded-2xl"
              style={{
                background: '#fff',
                border: '1px solid #f1f5f9',
                boxShadow: `0 2px 8px ${shadow}, 0 8px 24px rgba(0,0,0,0.06)`,
              }}>
              {/* icon + value row */}
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: bg, color }}>
                  {icon}
                </div>
                <span className="font-black leading-none" style={{
                  background: grad,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  fontSize: '30px',
                  letterSpacing: '-1px',
                  fontFamily: 'Inter, sans-serif',
                  fontVariantNumeric: 'tabular-nums',
                }}>{value}</span>
              </div>
              {/* label + sub */}
              <div>
                <p style={{ color: '#1e293b', fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{label}</p>
                <p style={{ color: '#94a3b8', fontSize: '11px', marginTop: '2px', fontFamily: 'Inter, sans-serif' }}>{sub}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter bar */}
      {!loading && globalStats.total > 0 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 mx-0">
          {/* Tab pills */}
          <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
            {[
              { key: 'all', label: 'All', count: globalStats.total },
              { key: 'published', label: 'Published', count: globalStats.published },
              { key: 'draft', label: 'Drafts', count: globalStats.draft },
              { key: 'archived', label: 'Archived', count: globalStats.archived },
            ].map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => { setFilter(key); setStoriesPage(1); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold transition-all"
                style={{
                  background: filter === key ? '#fff' : 'transparent',
                  color: filter === key ? '#1e293b' : '#64748b',
                  boxShadow: filter === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                  fontSize: '13px',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {label}
                <span className="px-1.5 py-0.5 rounded-md font-bold"
                  style={{
                    background: filter === key ? 'var(--brand-50)' : 'transparent',
                    color: filter === key ? 'var(--brand-primary)' : '#94a3b8',
                    fontSize: '11px',
                    fontFamily: 'Inter, sans-serif',
                  }}>
                  {count}
                </span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search stories…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full rounded-xl pl-9 pr-4"
              style={{
                background: '#fff',
                border: '1.5px solid #e2e8f0',
                color: '#1e293b',
                outline: 'none',
                height: '38px',
                fontSize: '13px',
                fontFamily: 'Inter, sans-serif',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.1)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
            />
          </div>

          <div className="sm:ml-auto flex items-center gap-2">
            <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
              {storiesTotal} {storiesTotal === 1 ? 'story' : 'stories'}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="relative w-14 h-14 mx-auto mb-5">
              <div className="absolute inset-0 rounded-full" style={{ background: 'var(--brand-50)' }} />
              <div className="absolute inset-0 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
              <div className="absolute inset-2 rounded-full flex items-center justify-center" style={{ background: 'var(--brand-50)' }}>
                <BookOpen size={16} style={{ color: 'var(--brand-primary)' }} />
              </div>
            </div>
            <p style={{ color: '#1e293b', fontSize: '14px', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Loading your stories…</p>
            <p style={{ color: '#94a3b8', fontSize: '12px', marginTop: '4px', fontFamily: 'Inter, sans-serif' }}>Hang tight, this won't take long</p>
          </div>
        </div>

      ) : globalStats.total === 0 ? (
        <div className="flex items-center justify-center py-24">
          <div className="text-center max-w-md">
            {/* illustration box */}
            <div className="relative w-28 h-28 mx-auto mb-6">
              <div className="absolute inset-0 rounded-3xl" style={{ background: 'linear-gradient(135deg,var(--brand-50),#f5f3ff)' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <BookOpen size={44} style={{ color: 'var(--brand-primary)', opacity: 0.5 }} />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,var(--brand-primary),#8b5cf6)', boxShadow: '0 4px 12px rgba(99,102,241,0.4)' }}>
                <Sparkles size={14} style={{ color: '#fff' }} />
              </div>
            </div>
            <h2 className="mb-2" style={{ color: '#0f172a', fontSize: '22px', fontWeight: 700, fontFamily: 'Inter, sans-serif', letterSpacing: '-0.3px' }}>No stories yet</h2>
            <p className="mb-8 leading-relaxed" style={{ color: '#64748b', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>
              Create your first AMP Web Story. Add pages, layers, animations, polls, and export valid AMP HTML.
            </p>
            <button onClick={createStory} disabled={creating}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all mx-auto"
              style={{ background: 'linear-gradient(135deg,var(--brand-primary),#8b5cf6)', color: '#fff', boxShadow: '0 4px 14px rgba(99,102,241,0.4)' }}>
              <Plus size={16} />
              Create your first story
            </button>
            <div className="flex flex-wrap justify-center gap-2 mt-8">
              {['Visual Editor', 'AMP Export', 'Polls & Quizzes', 'Templates', 'Animations'].map(f => (
                <span key={f} className="px-3 py-1 text-xs font-medium rounded-full"
                  style={{ background: '#f8faff', color: 'var(--brand-primary)', border: '1px solid var(--brand-100)' }}>
                  {f}
                </span>
              ))}
            </div>
          </div>
        </div>

      ) : (
        <>
          {/* Story grid */}
          <div className="mb-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>

            {/* Create card */}
            <button
              onClick={createStory}
              disabled={creating}
              className="group rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 flex flex-col text-left"
              style={{ aspectRatio: '9/17', border: '2px dashed var(--brand-100)', background: '#fafbff', color: '#94a3b8' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-primary)'; e.currentTarget.style.background = '#f0f2ff'; e.currentTarget.style.color = 'var(--brand-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--brand-100)'; e.currentTarget.style.background = '#fafbff'; e.currentTarget.style.color = '#94a3b8'; }}
            >
              <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all group-hover:scale-110"
                  style={{ background: 'linear-gradient(135deg,var(--brand-primary),#8b5cf6)', boxShadow: '0 4px 14px rgba(99,102,241,0.35)', color: '#fff' }}>
                  {creating ? <Loader2 size={20} className="animate-spin" /> : <Plus size={20} />}
                </div>
                <span style={{ fontSize: '13px', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>New Story</span>
              </div>
            </button>

            {/* Story cards */}
            {stories.map(story => (
              <div
                key={story.id}
                onClick={() => navigate(`/marketing/story-builder/${story.id}/view`)}
                className="group relative rounded-2xl overflow-hidden cursor-pointer"
                style={{
                  aspectRatio: '9/17',
                  background: '#111',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  transition: 'transform 0.22s cubic-bezier(.34,1.56,.64,1), box-shadow 0.22s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 16px 48px rgba(0,0,0,0.22)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }}
              >
                {/* media */}
                {story.thumbnail ? (
                  <img src={story.thumbnail} alt={story.title} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0">
                    <MiniPagePreview background={story.firstPageBackground} layers={story.firstPageLayers} />
                  </div>
                )}

                {/* dark hover overlay */}
                <div
                  className="absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                  style={{ background: 'rgba(0,0,0,0.55)' }}
                />

                {/* top: status + right-side action buttons */}
                <div className="absolute top-0 inset-x-0 z-20 flex items-start justify-between p-2">
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wide backdrop-blur-md"
                    style={{
                      background: story.status === 'published'
                        ? 'rgba(16,185,129,0.85)'
                        : story.status === 'archived'
                          ? 'rgba(100,116,139,0.85)'
                          : 'rgba(0,0,0,0.4)',
                      color: '#fff',
                    }}>
                    {story.status === 'published' ? '● Live' : story.status === 'archived' ? '◼ Archived' : '○ Draft'}
                  </span>

                  {/* right column — visible on hover */}
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <button onClick={e => { e.stopPropagation(); navigate(`/marketing/story-builder/${story.id}/edit`); }}
                      className="w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-md transition-colors"
                      style={{ background: 'rgba(255,255,255,0.18)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-primary)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                      title="Edit">
                      <Pencil size={11} className="text-white" />
                    </button>
                    <button onClick={e => duplicateStory(story.id, e)}
                      className="w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-md transition-colors"
                      style={{ background: 'rgba(255,255,255,0.18)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.35)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                      title="Duplicate">
                      <Copy size={11} className="text-white" />
                    </button>
                    <button onClick={e => deleteStory(story.id, e)}
                      className="w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-md transition-colors"
                      style={{ background: 'rgba(255,255,255,0.18)' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#ef4444'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
                      title="Delete">
                      <Trash2 size={11} className="text-white" />
                    </button>

                    {/* divider */}
                    <div style={{ height: 1, background: 'rgba(255,255,255,0.2)', margin: '2px 4px' }} />

                    {/* unpublish — only for published stories */}
                    {story.status === 'published' && (
                      <button onClick={e => unpublishStoryCard(story.id, e)}
                        className="w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-md transition-colors"
                        style={{ background: 'rgba(239,68,68,0.75)' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#ef4444'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.75)'}
                        title="Unpublish">
                        {unpublishing[story.id]
                          ? <Loader2 size={11} className="text-white animate-spin" />
                          : <GlobeOff size={11} className="text-white" />}
                      </button>
                    )}
                  </div>
                </div>

                {/* bottom gradient + info */}
                <div className="absolute bottom-0 inset-x-0 z-20 px-3 pb-3 pt-12"
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)' }}>
                  <p className="truncate mb-2" style={{ color: '#fff', fontSize: '12px', fontWeight: 600, fontFamily: 'Inter, sans-serif', lineHeight: '1.3', letterSpacing: '-0.1px' }}>
                    {story.title}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={e => { e.stopPropagation(); window.open(story.storyMetadata?.cloudflareUrl || story.storyMetadata?.publishedUrl || `${PLAYER_URL}/${story.id}`, '_blank'); }}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition-opacity hover:opacity-90"
                      style={{ background: 'linear-gradient(135deg,var(--brand-primary),#8b5cf6)', color: '#fff' }}
                      title="Play">
                      <Play size={8} fill="currentColor" /> Play
                    </button>
                    <button
                      onClick={e => publishStoryCard(story.id, e)}
                      className="w-6 h-6 rounded-full flex items-center justify-center transition-opacity hover:opacity-90"
                      style={{ background: story.status === 'published' ? 'rgba(16,185,129,0.9)' : 'rgba(255,255,255,0.22)' }}
                      title={story.status === 'published' ? 'Re-publish' : 'Publish'}>
                      {publishing[story.id] ? <Loader2 size={9} className="text-white animate-spin" /> : <Globe size={9} className="text-white" />}
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Empty filter state */}
            {stories.length === 0 && !loading && (filter !== 'all' || search) && (
              <div className="col-span-full flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: '#f1f5f9' }}>
                  <Search size={22} style={{ color: '#94a3b8' }} />
                </div>
                <p style={{ color: '#1e293b', fontSize: '14px', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>No stories match your filter</p>
                <p style={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>Try adjusting your search or filter</p>
                <button
                  onClick={() => { setSearch(''); setFilter('all'); setStoriesPage(1); }}
                  className="mt-1 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{ background: 'var(--brand-50)', color: 'var(--brand-primary)', border: '1px solid var(--brand-200)' }}>
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalServerPages > 1 && (
            <div className="flex items-center justify-center gap-2 mb-8">
              <button
                onClick={() => setStoriesPage(p => Math.max(1, p - 1))}
                disabled={storiesPage <= 1}
                className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                style={{
                  background: storiesPage <= 1 ? '#f8faff' : '#fff',
                  border: '1.5px solid #e2e8f0',
                  color: storiesPage <= 1 ? '#cbd5e1' : '#1e293b',
                  cursor: storiesPage <= 1 ? 'not-allowed' : 'pointer',
                  boxShadow: storiesPage <= 1 ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                ← Prev
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalServerPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalServerPages || Math.abs(p - storiesPage) <= 2)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '…' ? (
                      <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-sm" style={{ color: '#94a3b8' }}>…</span>
                    ) : (
                      <button key={p} onClick={() => setStoriesPage(p)}
                        className="w-8 h-8 rounded-lg text-sm font-semibold transition-all"
                        style={{
                          background: p === storiesPage ? 'linear-gradient(135deg,var(--brand-primary),#8b5cf6)' : 'transparent',
                          color: p === storiesPage ? '#fff' : '#64748b',
                          boxShadow: p === storiesPage ? '0 2px 8px rgba(99,102,241,0.35)' : 'none',
                        }}>
                        {p}
                      </button>
                    )
                  )}
              </div>
              <button
                onClick={() => setStoriesPage(p => Math.min(totalServerPages, p + 1))}
                disabled={storiesPage >= totalServerPages}
                className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                style={{
                  background: storiesPage >= totalServerPages ? '#f8faff' : '#fff',
                  border: '1.5px solid #e2e8f0',
                  color: storiesPage >= totalServerPages ? '#cbd5e1' : '#1e293b',
                  cursor: storiesPage >= totalServerPages ? 'not-allowed' : 'pointer',
                  boxShadow: storiesPage >= totalServerPages ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                Next →
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={doDeleteStory}
        title="Delete story?"
        message="This story will be permanently deleted. This cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );
}
